import { useEffect, useRef, useCallback, useMemo } from 'react'
import * as maplibregl from 'maplibre-gl'
// maplibre-gl 6 resolves its worker relative to import.meta.url, which does not
// survive bundling (and Vite 8/Rolldown mangles inline blob workers). Let Vite
// bundle the shipped ESM worker as its own asset and hand maplibre that URL.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { Protocol } from 'pmtiles'
import { createMapStyle, getMapColors, type MapTheme } from '@/lib/mapStyle'
import { useMapHoverHighlight } from './useMapHoverHighlight'
import { addVisitorLayers, startPulseAnimation, SOURCES } from './visitorMapLayers'
import { setupMarkerInteractions, formatLastSeen } from './useMapMarkerInteractions'
import type { Visitor, VisitorFeature } from '@/components/visitors/types'

maplibregl.setWorkerUrl(maplibreWorkerUrl)

interface UseVisitorMapOptions {
  container: HTMLDivElement | null
  pmtilesUrl: string
  visitors: Visitor[]
  theme?: MapTheme
  hoveredId?: string | null
  onVisitorClick?: (visitorId: string) => void
  onMapReady?: (map: maplibregl.Map) => void
}

/** Calculate bounding box of visitor locations for fitBounds */
function calculateBounds(visitors: Visitor[]): {
  bounds: [[number, number], [number, number]] | null
  center: [number, number]
  hasLocations: boolean
} {
  const withCoords = visitors.filter(v => v.location)
  if (withCoords.length === 0) {
    return { bounds: null, center: [0, 20], hasLocations: false }
  }

  const sumLng = withCoords.reduce((sum, v) => sum + v.location!.longitude, 0)
  const sumLat = withCoords.reduce((sum, v) => sum + v.location!.latitude, 0)
  const center: [number, number] = [sumLng / withCoords.length, sumLat / withCoords.length]

  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity
  for (const v of withCoords) {
    const { longitude: lng, latitude: lat } = v.location!
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
  }

  return {
    bounds: [[minLng, minLat], [maxLng, maxLat]],
    center,
    hasLocations: true,
  }
}

export function useVisitorMap({
  container,
  pmtilesUrl,
  visitors,
  theme = 'dark',
  hoveredId,
  onVisitorClick,
  onMapReady,
}: UseVisitorMapOptions) {
  const mapRef = useRef<maplibregl.Map | null>(null)
  const protocolRef = useRef<Protocol | null>(null)
  const initializedRef = useRef(false)
  const visitorsRef = useRef(visitors)
  const currentThemeRef = useRef(theme)

  // Compute live visitors (seen within last 5 minutes - from backend)
  const { liveVisitors, liveCount } = useMemo(() => {
    const live = visitors.filter(v => v.location && v.live)
    return { liveVisitors: live, liveCount: live.length }
  }, [visitors])

  // Convert visitor to GeoJSON feature
  const toFeature = useCallback((v: Visitor): VisitorFeature => ({
    type: 'Feature',
    id: v.id,
    geometry: {
      type: 'Point',
      coordinates: [v.location!.longitude, v.location!.latitude],
    },
    properties: {
      id: v.id,
      name: v.name,
      active: v.active,
      live: v.live,
      city: v.location!.city,
      country_code: v.location!.country_code,
      last_seen_at: v.last_seen_at,
    },
  }), [])

  // Get GeoJSON for live visitors only
  const getLiveGeoJSON = useCallback((): GeoJSON.FeatureCollection<GeoJSON.Point> => {
    const features = visitors
      .filter(v => v.location && v.live)
      .map(toFeature)
    return { type: 'FeatureCollection', features }
  }, [visitors, toFeature])

  // Get GeoJSON for active non-live visitors only
  const getActiveGeoJSON = useCallback((): GeoJSON.FeatureCollection<GeoJSON.Point> => {
    const features = visitors
      .filter(v => v.location && !v.live)
      .map(toFeature)
    return { type: 'FeatureCollection', features }
  }, [visitors, toFeature])

  // Initialize map
  useEffect(() => {
    // Handle theme changes by destroying and recreating
    if (currentThemeRef.current !== theme && mapRef.current) {
      mapRef.current.remove()
      mapRef.current = null
      if (protocolRef.current) {
        maplibregl.removeProtocol('pmtiles')
        protocolRef.current = null
      }
      initializedRef.current = false
    }
    currentThemeRef.current = theme

    if (!container || initializedRef.current) return

    const colors = getMapColors(theme)

    const initMap = async () => {
      // Register PMTiles protocol
      const protocol = new Protocol()
      maplibregl.addProtocol('pmtiles', protocol.tile)
      protocolRef.current = protocol

      const style = await createMapStyle(pmtilesUrl, theme)
      const { bounds, center, hasLocations } = calculateBounds(visitorsRef.current)

      const map = new maplibregl.Map({
        container,
        style,
        center,
        zoom: 2,
        attributionControl: false,
      })

      // Fit bounds to show all visitors once map is loaded
      if (hasLocations && bounds) {
        map.on('load', () => {
          map.fitBounds(bounds, {
            padding: { top: 80, bottom: 80, left: 80, right: 80 },
            maxZoom: 12,
            duration: 0,
          })
        })
      }

      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right')

      map.on('load', () => {
        // Add active (non-live) visitors source with clustering
        map.addSource(SOURCES.ACTIVE, {
          type: 'geojson',
          data: getActiveGeoJSON(),
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        })

        // Add live visitors source with tighter clustering
        map.addSource(SOURCES.LIVE, {
          type: 'geojson',
          data: getLiveGeoJSON(),
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 30, // Tighter clustering for live visitors
        })

        // Add hovered visitor source (non-clustered)
        map.addSource(SOURCES.HOVERED, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })

        // Add all layers
        addVisitorLayers(map, colors)
        startPulseAnimation(map)

        // Setup click handlers, popups, and cursor changes
        setupMarkerInteractions({ map, colors, onVisitorClick, formatLastSeen })

        onMapReady?.(map)
      })

      mapRef.current = map
      initializedRef.current = true
    }

    initMap()

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      if (protocolRef.current) {
        maplibregl.removeProtocol('pmtiles')
        protocolRef.current = null
      }
      initializedRef.current = false
    }
  }, [container, pmtilesUrl, theme, onMapReady])

  // Update visitors data when it changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return

    const activeSource = map.getSource(SOURCES.ACTIVE) as maplibregl.GeoJSONSource
    const liveSource = map.getSource(SOURCES.LIVE) as maplibregl.GeoJSONSource

    if (activeSource) {
      activeSource.setData(getActiveGeoJSON())
    }
    if (liveSource) {
      liveSource.setData(getLiveGeoJSON())
    }
  }, [visitors, getActiveGeoJSON, getLiveGeoJSON])

  // Handle hover highlighting (extracted to separate hook)
  useMapHoverHighlight({ mapRef, hoveredId, visitors })

  return { mapRef, liveCount, liveVisitors }
}
